<template>
  <button
    v-on-click-outside="() => (showMenu = false)"
    type="button"
    :disabled="!statuses?.length || viewOnly"
    :class="status || 'not-selected'"
    class="flex items-center justify-center space-x-2 rounded-full px-3 py-1.5 text-xxs focus:outline-hidden"
    @click.stop="showMenu = !showMenu"
  >
    <span class="uppercase">
      {{ status || placeholder }}
    </span>
    <div v-if="showInfoIcon" v-tooltip="errorDescription">
      <icon-base
        :icon="IconInfoSmall"
        view-box="0 0 14 14"
        class="translate-y-0-25 transform text-red-400"
      />
    </div>
    <span
      v-if="statuses?.length && !viewOnly"
      class="inset-y-0 flex cursor-pointer place-self-center"
    >
      <icon-base height="6" width="10" :icon="IconDropdownArrow" />
    </span>
  </button>

  <ul
    v-if="!viewOnly && showMenu && statuses?.length"
    ref="menuElement"
    class="z-50 w-26 rounded-md bg-white py-2 text-sm text-gray-500 shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:w-40 sm:text-sm"
  >
    <li
      v-for="(status, index) in statuses"
      :key="index"
      class="flex justify-start overflow-auto py-2 pr-2 hover:bg-gray-200"
      role="option"
      :aria-selected="modelValue == status"
      @click="selectStatus(status)"
    >
      <span class="ml-3 cursor-pointer font-normal">{{ status }}</span>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { getPlacementErrorMessage } from "@/helpers/placements";
import { useTeleportV2 } from "@/hooks/elements";

import IconDropdownArrow from "@/components/icons/IconDropdownArrow.vue";
import IconInfoSmall from "@/components/icons/IconInfoSmall.vue";
import IconBase from "../ui/IconBase.vue";

import type { ILenderPlacement } from "@/models/funders";
import type { PropType } from "vue";

const STATUS_ERROR = "error";

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: "",
  },
  statuses: {
    type: Array as PropType<string[]>,
  },
  status: String,
  model: {
    type: Object as PropType<ILenderPlacement>,
  },
  viewOnly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "statusSelected"]);

const placeholder = "status";

const { t } = useI18n();

const showMenu = ref(false);
const menuElement = ref<HTMLUListElement | null>(null);

useTeleportV2({
  target: menuElement,
  to: "#modals",
  offset: { top: 5 },
});

const showInfoIcon = computed(
  () => props.status?.toLowerCase() === STATUS_ERROR
);

const errorDescription = computed(() => {
  if (!props.model) {
    return t("COMMON.ERROR_MSG_UNAVAILABLE");
  }

  return (
    getPlacementErrorMessage(props.model) ?? t("COMMON.ERROR_MSG_UNAVAILABLE")
  );
});

const selectStatus = (status: string) => {
  showMenu.value = false;
  emit("update:modelValue", status);
  emit("statusSelected", status);
};
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.Pending {
  background-color: rgba(255, 159, 4, 0.1);
  color: rgb(255, 159, 4);
}

.Approved {
  background-color: rgba(6, 214, 160, 0.1);
  color: rgb(6, 214, 160);
}

.Declined {
  background-color: rgba(255, 89, 94, 0.1);
  color: rgb(255, 89, 94);
}

.Error {
  background-color: rgba(255, 89, 94, 0.1);
  color: rgb(255, 89, 94);
}

.not-selected {
  background-color: rgb(243, 244, 246);
}
</style>
